/**
 * LanguageSwitcher — anonymous + authenticated language picker.
 *
 * Anonymous users (landing page): renders as a compact dropdown in the
 * MarketingNav. Persists choice to localStorage; refresh honours it (per spec §8.1).
 *
 * Authenticated users have a richer in-app version inside /settings.
 *
 * Only ACTIVATED locales render — first-draft locales are hidden unless
 * ?i18n_preview=1 is set in the URL. The "Preview" badge surfaces on
 * first-draft locales so reviewers can spot them.
 */
import { useState, useRef, useEffect } from "react";
import { Globe, Check } from "lucide-react";

import { useI18n } from "../lib/i18n";

export default function LanguageSwitcher({
  testid = "language-switcher",
  compact = false,
}) {
  const { locale, setLocale, languages } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Defensive: if NO locales are activated (e.g., production misconfig), render nothing.
  if (!languages || languages.length < 1) return null;

  const active = languages.find((l) => l.code === locale) || languages[0];

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-toggle`}
        className={
          (compact
            ? "px-2.5 py-1.5 text-xs"
            : "px-3 py-2 text-sm") +
          " inline-flex items-center gap-1.5 rounded-full border border-[#2C2B29]/15 bg-white/80 hover:bg-white transition-colors"
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe size={13} strokeWidth={1.6} className="text-[#525252]" />
        <span className="font-medium text-[#171717]">{active.autonym}</span>
      </button>
      {open && (
        <ul
          role="listbox"
          data-testid={`${testid}-menu`}
          className="absolute right-0 mt-2 min-w-[200px] max-h-80 overflow-y-auto rounded-2xl border hairline bg-white shadow-lg p-1.5 z-50"
        >
          {languages.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                role="option"
                aria-selected={locale === l.code}
                data-testid={`${testid}-option-${l.code}`}
                onClick={() => {
                  setLocale(l.code);
                  setOpen(false);
                }}
                className={
                  "w-full text-left px-3 py-2 rounded-xl text-sm transition-colors flex items-center justify-between gap-3 " +
                  (locale === l.code
                    ? "bg-[#F2EFEA] text-[#171717]"
                    : "hover:bg-[#F8F8F5] text-[#525252]")
                }
              >
                <span className="flex-1 truncate">{l.autonym}</span>
                {!l.activated && (
                  <span className="text-[10px] uppercase tracking-wider text-[#B58A3F] bg-[#F4E5C5]/60 px-1.5 py-0.5 rounded-full">
                    preview
                  </span>
                )}
                {locale === l.code && <Check size={12} strokeWidth={1.6} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
